import type { TicketScope } from '../serviceCalls/types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { apiService } from '../../services/ApiService';
import { useTranslation } from '../../contexts/TranslationContext';
import { showErrorToast, showSuccessToast } from '../shared/toast';
import { toTree, flatten, parentOptions, type SystemRow, type SystemNode } from './systemTreeClient';

/**
 * SystemTreePanel — the system tree, and the only place a person can shape it by hand
 * (migration 058).
 *
 * WHY IT IS SEPARATE FROM THE REST OF SystemsPage. That screen renders systems DERIVED from
 * POI property strings; this reads the `systems` table itself. They answer different
 * questions — "what did the derivation produce" versus "what is the tree" — and merging them
 * would hide which one you are looking at.
 *
 * THE DERIVATION GATE LIVES HERE TOO. A derivation writing straight into an arbitrary-depth
 * tree is how one site ended up with 46 systems named after Revit families. Preview shows
 * what it WOULD build, with the three numbers that say it is wrong before it is written:
 * how many systems, how deep, and how many assets it leaves with no system at all.
 */

interface Props {
  focusedSystemId?: number | null;
  onFocusHandled?: () => void;
  canEdit?: boolean;
  onOpenTickets?: (scope: TicketScope) => void;
  onCreateTicket?: (scope: TicketScope) => void;
  assetId: string;
  /** The property map currently configured on the Set Systems form, for the preview. */
  systemProperty?: string;
  subcategoryLevels?: string[];
  /** Run the real assignment once the preview has been accepted. */
  onApplyDerivation?: () => void;
}

interface PreviewNode { name: string; path: string[]; assetCount: number; children: PreviewNode[] }
interface Preview {
  tree: PreviewNode[]; systemCount: number; maxDepth: number; skipped: number; total: number;
}

const INDENT = 18;

export const SystemTreePanel: React.FC<Props> = ({
  assetId, systemProperty, subcategoryLevels, onApplyDerivation, canEdit = true, onOpenTickets, onCreateTicket, focusedSystemId, onFocusHandled,
}) => {
  const { t } = useTranslation();
  const [rows, setRows] = useState<SystemRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [newName, setNewName] = useState('');
  const [newParent, setNewParent] = useState<string>('');
  const [editing, setEditing] = useState<{ id: number; name: string } | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [previewing, setPreviewing] = useState(false);

  const refresh = useCallback(async () => {
    if (!assetId) return;
    setLoading(true);
    try {
      const list = await apiService.get(`/assets/${assetId}/systems`);
      setRows(Array.isArray(list) ? list : []);
    } catch (err: any) {
      showErrorToast(err?.message || 'Failed to load systems');
    } finally {
      // Always cleared — a loading flag left true is how a pane says "Loading…" forever.
      setLoading(false);
    }
  }, [assetId]);

  useEffect(() => { void refresh(); }, [refresh]);

  const focusHandledRef = useRef(onFocusHandled);
  focusHandledRef.current = onFocusHandled;
  useEffect(() => {
    if (!focusedSystemId || loading || !rows.length) return;
    const row = document.getElementById(`ticket-system-${focusedSystemId}`);
    if (row) { row.scrollIntoView({ block: 'center' }); row.focus({ preventScroll: true }); }
    focusHandledRef.current?.();
  }, [focusedSystemId, loading, rows]);

  const create = async () => {
    const name = newName.trim();
    if (!name) return;
    try {
      await apiService.post(`/assets/${assetId}/systems`, {
        name, parentId: newParent === '' ? null : Number(newParent),
      });
      setNewName('');
      await refresh();
    } catch (err: any) {
      showErrorToast(err?.message || 'Could not create the system');
    }
  };

  const patch = async (id: number, changes: Record<string, unknown>) => {
    try {
      await apiService.patch(`/assets/${assetId}/systems/${id}`, changes);
      await refresh();
    } catch (err: any) {
      // The server refuses a cycle and a duplicate sibling; its message is the useful one.
      showErrorToast(err?.message || 'Could not update the system');
    }
  };

  const remove = async (node: SystemNode) => {
    try {
      await apiService.delete(`/assets/${assetId}/systems/${node.id}`);
      await refresh();
    } catch (err: any) {
      showErrorToast(err?.message || 'Could not delete the system');
    }
  };

  const runPreview = async () => {
    if (!systemProperty) {
      showErrorToast(t('systemsPickPropertyFirst', 'Choose a system property first'));
      return;
    }
    setPreviewing(true);
    try {
      const result = await apiService.post(`/assets/${assetId}/pois/reassign-systems/preview`, {
        systemProperty, subcategoryLevels,
      });
      setPreview(result);
    } catch (err: any) {
      showErrorToast(err?.message || 'Preview failed');
    } finally {
      setPreviewing(false);
    }
  };

  const tree = toTree(rows);
  const flat = flatten(tree);

  const renderPreview = (nodes: PreviewNode[], depth = 0): React.ReactNode =>
    nodes.map((node) => (
      <React.Fragment key={node.path.join('/')}>
        <div style={{
          display: 'flex', gap: 8, fontSize: 13, padding: '3px 0',
          paddingInlineStart: depth * INDENT, color: 'var(--dt-text-2)',
        }}>
          <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {node.name}
          </span>
          <span style={{ color: 'var(--dt-text-3)', fontVariantNumeric: 'tabular-nums' }}>
            {node.assetCount}
          </span>
        </div>
        {renderPreview(node.children, depth + 1)}
      </React.Fragment>
    ));

  return (
    <section className="dt-card" style={{ padding: 16, marginBottom: 18 }}>
      <h3 style={{ margin: '0 0 4px', fontSize: '1rem' }}>
        {t('systemsTreeTitle', 'System tree')}
      </h3>
      <p style={{ margin: '0 0 12px', fontSize: 12, color: 'var(--dt-text-3)' }}>
        {t('systemsTreeHint',
          'Group systems under a parent — an asset in a child is also in everything above it, '
          + 'and whoever is responsible for a parent is responsible for what is below it.')}
      </p>

      {/* CREATE */}
      {canEdit && <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <input
          className="dt-field"
          style={{ flex: '1 1 180px' }}
          value={newName}
          placeholder={t('systemsNewName', 'New system name')}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') void create(); }}
        />
        <select
          className="dt-field"
          style={{ flex: '0 1 200px' }}
          value={newParent}
          onChange={(e) => setNewParent(e.target.value)}
        >
          <option value="">{t('systemsNoParent', 'Top level')}</option>
          {parentOptions(rows, null).map((node) => (
            <option key={node.id} value={node.id}>
              {`${' '.repeat(node.depth * 2)}${node.name}`}
            </option>
          ))}
        </select>
        <button type="button" className="dt-btn pri sm" onClick={() => void create()} disabled={!newName.trim()}>
          {t('systemsAdd', 'Add')}
        </button>
      </div>}

      {/* THE TREE */}
      {loading && rows.length === 0 && (
        <div className="dt-muted">{t('loading', 'Loading…')}</div>
      )}
      {!loading && rows.length === 0 && (
        <div className="dt-muted">
          {t('systemsTreeEmpty', 'No systems yet. Add one above, or derive them from model properties below.')}
        </div>
      )}

      {flat.map((node) => (
        <div
          key={node.id}
          id={`ticket-system-${node.id}`} tabIndex={-1}
          className={focusedSystemId === node.id ? 'dt-ticket-scope-focus' : undefined}
          style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0',
            paddingInlineStart: node.depth * INDENT,
            borderTop: '1px solid var(--dt-border)',
          }}
        >
          {editing?.id === node.id ? (
            <input
              className="dt-field"
              style={{ flex: 1 }}
              autoFocus
              value={editing.name}
              onChange={(e) => setEditing({ id: node.id, name: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && editing.name.trim()) {
                  void patch(node.id, { name: editing.name.trim() });
                  setEditing(null);
                }
                if (e.key === 'Escape') setEditing(null);
              }}
              onBlur={() => setEditing(null)}
            />
          ) : (
            <button
              type="button"
              disabled={!canEdit}
              onClick={() => setEditing({ id: node.id, name: node.name })}
              title={t('systemsRename', 'Rename')}
              style={{
                flex: 1, minWidth: 0, textAlign: 'start', background: 'transparent',
                border: 'none', color: 'var(--dt-text)', font: 'inherit', cursor: 'text',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}
            >
              {node.name}
            </button>
          )}

          {/* Derived systems are marked, because a re-run of the derivation may replace them
              while anything authored by hand is left alone. */}
          {node.source === 'derived' && (
            <span
              title={t('systemsDerivedHint', 'Created by a property derivation — a re-run may replace it')}
              style={{ fontSize: 11, color: 'var(--dt-text-3)', border: '1px solid var(--dt-border)', borderRadius: 999, padding: '1px 7px' }}
            >
              {t('systemsDerived', 'derived')}
            </span>
          )}

          <span
            title={t('systemsCountHint', 'Assets in this system or one below it')}
            style={{ fontSize: 12, color: 'var(--dt-text-3)', fontVariantNumeric: 'tabular-nums' }}
          >
            {node.rollupCount}
          </span>

          {onOpenTickets && <button className="dt-btn sm" onClick={() => onOpenTickets({
            scope_type: 'system', scope_id: String(node.id), name: node.name,
          })}>{t('tickets')}</button>}
          {onCreateTicket && <button className="dt-btn sm" onClick={() => onCreateTicket({
            scope_type: 'system', scope_id: String(node.id), name: node.name,
          })}>{t('dt_tickets_new')}</button>}
          {canEdit && <>
          <select
            className="dt-field"
            style={{ width: 170, padding: '4px 8px', fontSize: 12 }}
            value={node.parent_id === null ? '' : String(node.parent_id)}
            onChange={(e) => void patch(node.id, { parentId: e.target.value === '' ? null : Number(e.target.value) })}
            aria-label={t('systemsParent', 'Parent')}
          >
            <option value="">{t('systemsNoParent', 'Top level')}</option>
            {/* Itself and its own descendants are not offered — the server would refuse them
                with a 409, and a dropdown that offers impossible choices is a worse form. */}
            {parentOptions(rows, node.id).map((option) => (
              <option key={option.id} value={option.id}>
                {`${' '.repeat(option.depth * 2)}${option.name}`}
              </option>
            ))}
          </select>

          <button
            type="button"
            className="dt-btn sm dngr"
            onClick={() => void remove(node)}
            title={node.rollupCount > 0
              ? t('systemsDeleteBlocked', 'Assets are still in this system')
              : t('delete', 'Delete')}
          >
            ✕
          </button>
          </>}
        </div>
      ))}

      {/* THE DERIVATION GATE */}
      {canEdit && systemProperty && (
        <div style={{ marginTop: 16, borderTop: '1px solid var(--dt-border)', paddingTop: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <strong style={{ flex: 1, fontSize: '.9rem' }}>
              {t('systemsPreviewTitle', 'Derive from model properties')}
            </strong>
            <button type="button" className="dt-btn sm" onClick={() => void runPreview()} disabled={previewing}>
              {previewing ? t('systemsPreviewing', 'Checking…') : t('systemsPreview', 'Preview')}
            </button>
          </div>

          {preview && (
            <>
              <div style={{ display: 'flex', gap: 14, fontSize: 12, color: 'var(--dt-text-2)', marginBottom: 8, flexWrap: 'wrap' }}>
                <span>{t('systemsWouldCreate', 'Systems')}: <strong>{preview.systemCount}</strong></span>
                <span>{t('systemsMaxDepth', 'Depth')}: <strong>{preview.maxDepth}</strong></span>
                <span style={{ color: preview.skipped > 0 ? 'var(--dt-warn)' : undefined }}>
                  {t('systemsNoSystem', 'Assets with no system')}: <strong>{preview.skipped}</strong>
                  {` / ${preview.total}`}
                </span>
              </div>

              {/* The number that says "this derivation is wrong" before it is written. */}
              {preview.systemCount > 30 && (
                <div style={{
                  padding: '8px 10px', marginBottom: 8, borderRadius: 'var(--dt-r-sm)',
                  border: '1px solid var(--dt-warn)', color: 'var(--dt-warn)', fontSize: 12,
                }}>
                  {t('systemsTooManyWarning',
                    'That is a lot of systems for one site. Property values like Revit family '
                    + 'names produce one system per element type rather than real groupings — '
                    + 'check the names below before applying.')}
                </div>
              )}

              <div style={{ maxHeight: 220, overflowY: 'auto', marginBottom: 10 }}>
                {renderPreview(preview.tree)}
              </div>

              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  className="dt-btn pri sm"
                  onClick={() => { onApplyDerivation?.(); setPreview(null); }}
                  disabled={!onApplyDerivation || preview.systemCount === 0}
                >
                  {t('systemsApplyDerivation', 'Apply this')}
                </button>
                <button type="button" className="dt-btn sm" onClick={() => setPreview(null)}>
                  {t('cancel', 'Cancel')}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </section>
  );
};

export default SystemTreePanel;
